import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import useRipple from "../../hooks/useRipple.js";
import "../../hooks/useRipple.css";
import { getAccountType } from "../../store/auth/selectors";
import { checkPermission } from "../../shared/utils/permissions";
import { PERMISSION_TYPES } from "../../shared/utils/permissions";
import useTeamHierarchyQueries from "../../store/teams/teamsQueries";
import StudentTable from "../../shared/components/Table/StudentTable";
import Pagination from "../../shared/components/Pagination/Pagination";
import UploadButton from "../../shared/components/UploadButton/UploadButton";
import search_icon from "../../assets/images/search_icon.svg";
import loader from "../../assets/gifs/loader.gif";

const TeamsHierarchyTabContent = () => {
  const accountType = useSelector(getAccountType);
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);

  const canExportTeamsData = checkUserPermission(
    PERMISSION_TYPES.EXPORT_TEAMS_DATA
  );
  const canUploadTeamsData = checkUserPermission(
    PERMISSION_TYPES.UPLOAD_TEAMS_DATA
  );

  const [localSearchInput, setLocalSearchInput] = useState("");
  const {
    hierarchyHeaders,
    hierarchyData,
    totalHierarchyCount,
    isDownloadLoading,
    downloadError,
    handleDownload,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleLimitChange,
    // Search related
    searchTerm,
    handleSearchChange,
    // Raw data for export
    rawCsvData,
    // Upload related
    // handleUpload,
    // isUploadLoading,
    // uploadError,
  } = useTeamHierarchyQueries(1, 50);

  const exportCsvBtnRef = useRef(null);
  useRipple(exportCsvBtnRef);

  const handleExportCSV = useCallback(() => {
    if (!rawCsvData) return;

    const blob = new Blob([rawCsvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "team_hierarchy.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [rawCsvData]);

  const columns = useMemo(() => {
    return hierarchyHeaders.map((header) => ({
      key: header,
      // Replace underscores with spaces and capitalize
      label: header.replace(/_/g, " ").toUpperCase(),
    }));
  }, [hierarchyHeaders]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      if (localSearchInput !== searchTerm) {
        handleSearchChange(localSearchInput);
      }
    }, 300);

    return () => {
      clearTimeout(timerId);
    };
  }, [localSearchInput, handleSearchChange, searchTerm]);

  useEffect(() => {
    setLocalSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    handleDownload();
  }, [handleDownload]);

  if (downloadError) {
    return (
      <div className="error-message">
        Error loading hierarchy:{" "}
        {downloadError.message ||
          JSON.stringify(downloadError) ||
          "Unknown error"}
      </div>
    );
  }

  return (
    <div className="teams-hierarchy-tab-content">
      <div className="filters-wrapper">
        <div className="search-wrapper">
          <img src={search_icon} alt="Search" className="search-icon" />
          <input
            type="text"
            placeholder="Search hierarchy..."
            value={localSearchInput}
            onChange={(e) => setLocalSearchInput(e.target.value)}
            className="schools-search-input"
          />
        </div>
        <div className="actions-wrapper">
          {canExportTeamsData && (
            <button
              ref={exportCsvBtnRef}
              onClick={handleExportCSV}
              className="export-btn"
              disabled={isDownloadLoading || !rawCsvData}
              style={{ position: "relative", overflow: "hidden" }}
            >
              Export CSV
            </button>
          )}
          {canUploadTeamsData && (
            <UploadButton
              uploadType="hierarchyUpload"
              selectedTab="Hierarchy"
            />
          )}
          <Pagination
            totalItems={totalHierarchyCount}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            itemsPerPageOptions={[10, 50, 100, 250]}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleLimitChange}
          />
        </div>
      </div>
      <div className="tableCont">
        {isDownloadLoading ? (
          <div className="loaderCont">
            <img
              src={loader}
              alt="Loading..."
              style={{
                width: "300px",
                height: "300px",
                margin: "auto",
                display: "block",
              }}
            />
          </div>
        ) : totalHierarchyCount === 0 && !isDownloadLoading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            {searchTerm
              ? `No results found for "${searchTerm}".`
              : "No hierarchy data loaded."}
          </div>
        ) : (
          <StudentTable
            data={hierarchyData}
            columns={columns}
            showRowSelector={false}
          />
        )}
      </div>
    </div>
  );
};

export default TeamsHierarchyTabContent;
